import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';

import { algoliaApiGetObject } from 'algolia-api/algoliaApiGetObject';
import { algoliaApiGetSearchConfig } from 'algolia-api/search/algoliaApiGetSearchConfig';
import { getVolopsSearchIndex } from 'algolia-api/search/getVolopsSearchIndex';
import { apiFetchListing } from 'api/apiListing';
import { ApiListing } from 'api/types/ApiListing';
import { ApiUnpublishedListing } from 'api/types/ApiUnpublishedListing';
import { ApiImportedListing } from 'api/volops';
import { setError } from 'modules/main/store/ducks/error';
import { SliceActions } from 'types/SliceActions';
import { LISTING_TYPE_URL_SEGMENT_MAP } from 'utils/constants';

// Types

export type MainStoreListingsState = {
  byId: Record<
    string,
    {
      listing:
        | ApiListing
        | ApiUnpublishedListing
        | ApiImportedListing
        | undefined;
      isFetching?: boolean;
    }
  >;
};

// Reducer

const listingsSlice = createSlice({
  name: 'listings',
  initialState: { byId: {} } as MainStoreListingsState,
  reducers: {
    fetchListingRequest: (
      state,
      action: PayloadAction<{ listingId: string }>,
    ) => {
      const { listingId } = action.payload;
      state.byId[listingId] = { listing: undefined, isFetching: true };
    },
    fetchListingSuccess: (
      state,
      action: PayloadAction<{ listing: ApiListing }>,
    ) => {
      const { listing } = action.payload;
      state.byId[listing.id] = { listing, isFetching: false };
    },
  },
});

export const listingsReducer = listingsSlice.reducer;

const {
  actions: { fetchListingRequest, fetchListingSuccess },
} = listingsSlice;

export type MainStoreListingsAction = SliceActions<
  typeof listingsSlice.actions
>;

// Functions

export function fetchListing(
  listingType: keyof typeof LISTING_TYPE_URL_SEGMENT_MAP,
  listingId: string,
  force = false,
) {
  return (
    dispatch: Dispatch,
    getState: () => { listings: MainStoreListingsState },
  ) => {
    const state = getState();
    const { listing, isFetching } = state.listings.byId[listingId] || {};

    if (force || (!listing && !isFetching)) {
      dispatch(fetchListingRequest({ listingId }));

      return apiFetchListing(listingType, listingId).then((fetchedListing) => {
        dispatch(fetchListingSuccess({ listing: fetchedListing }));

        return fetchedListing;
      });
    }

    return Promise.resolve(listing);
  };
}

export function fetchImportedListing(listingId: string, force = false) {
  return (
    dispatch: Dispatch,
    getState: () => { listings: MainStoreListingsState },
  ) => {
    const state = getState();
    const listing = state.listings.byId[listingId];

    if (!listing || (force && !listing.isFetching)) {
      dispatch(fetchListingRequest({ listingId }));
      const searchConfig = algoliaApiGetSearchConfig();
      const index = getVolopsSearchIndex(null, 'relevance');
      algoliaApiGetObject({
        config: searchConfig,
        index,
        id: listingId,
        attributes: ['*'],
      })
        .then((fetchedListing) => {
          dispatch(
            // @ts-expect-error TS(2345): Argument of type 'ImportedListing | null | undefin... Remove this comment to see the full error message
            fetchedListing as ApiImportedListing | null | undefined,
          );
        })
        .catch((e) => {
          dispatch(setError(e));
        });
    }
  };
}

export const updateListing = fetchListingSuccess;
